import { api_configuration } from "common";
import api_controls = api_configuration.libraries.gui_definition_lib.libraries.control_bundle_lib.libraries.controls_lib.types.control_library.alan_api;

export type ControlContract = (
	parent_dom_node: HTMLElement,
	view_root: HTMLElement,
	on_destroy: (callback: () => void) => void,
	data: any
) => ControlProperties;
export interface ControlNumberProperty {
	(value: number): void;
}
export interface ControlTextProperty {
	(value: string): void;
}
export interface ControlCollectionProperty {
	initialize: (
		on_destroy: Destructor,
		data: CollectionData
	) => {
		add: (
			key: string,
			destructor: Destructor,
			data: ControlData
		) => {
			entry: ControlProperties;
			remove: () => void;
		};
		sort?: (sorted: string[]) => void;
	};
}
export type ControlStateGroupProperty = Record<
	string,
	(destructor: Destructor, data: ControlData) => ControlProperties
>;
export type ControlInstructionProperty = (handler: (...args: any) => void) => void;
export type ControlMarkupProperty = () => HTMLElement;
export type ControlControlProperty = (destructor: Destructor, data: ControlData) => ControlProperties;
export type ControlProperty =
	| ControlNumberProperty
	| ControlTextProperty
	| ControlCollectionProperty
	| ControlStateGroupProperty
	| ControlInstructionProperty
	| ControlMarkupProperty
	| ControlControlProperty;
export type ControlProperties = Record<string, ControlProperty>;
export type Destructor = (destructed: () => void) => void;
export type CollectionData = {
	entries: Map<string, ControlData>;
	sort: string[];
};
export type StateData = { name: string; node: ControlData };
type InstructionData = (...args: any) => void;
type PropertyData = string | number | CollectionData | StateData | InstructionData | ControlData;
export type ControlData = { [key: string]: PropertyData };

let control_library: Record<string, api_controls.Ccontrol_library> | undefined = undefined;
export function loadControls(controls: Record<string, api_controls.Ccontrol_library>): void {
	control_library = controls;
}

export function getControl(
	name: string,
	parent: HTMLElementConstructor,
	view_root: HTMLElementConstructor,
	destructor: Destructor
): ControlProperties {
	if (control_library === undefined) {
		throw new Error("Control library is not initialized");
	}
	const control_definition = control_library[name];
	if (control_definition === undefined) {
		throw new Error(`Control ${name} not found`);
	}
	const control_implementation = global.control_implementations[name]!;
	const control_initializer = new ControlWrapper(
		control_implementation,
		parent,
		view_root,
		destructor,
		control_definition.properties.node
	);
	return control_initializer.properties;
}

let synching = false;
function requestSync() {
	if (synching) {
		return;
	}
	synching = true;
	requestAnimationFrame(() => {
		for (const deferred_call of deferred_control_calls) {
			deferred_call.syncData();
		}
		for (const deferred_call of deferred_control_calls) {
			deferred_call.doSync();
		}
		deferred_control_calls.clear();
		synching = false;
	});
}

export async function ControlsAreSynchronized(): Promise<void> {
	while (synching) {
		await new Promise<void>((resolve) => {
			requestAnimationFrame(() => {
				resolve();
			});
		});
	}
}

const deferred_control_calls = new Set<SynchronizedProperty>();
function onSyncData(property: SynchronizedProperty) {
	if (synching) {
		// Synchronize the data, because new control calls can be done while synchronizing.
		property.syncData();
	}
	deferred_control_calls.add(property);
}

function createPropertiesWrapperConstructor(
	node: api_controls.Ccontrol_node
): (data: ControlData, destructor: Destructor, getProperties: () => ControlProperties) => ControlProperties {
	const contract_prototype: ControlProperties = {};
	//TODO const data_prototype: ControlData = {};

	const instructions: ((
		contract: ControlProperties,
		data: ControlData,
		destructor: Destructor,
		getProperties: () => ControlProperties
	) => void)[] = [];

	for (const attribute of node.properties.attributes.values()) {
		const attribute_name = attribute.key;
		switch (attribute.properties.type.state.name) {
			case "control": {
				const $ = attribute.properties.type.state.node;
				contract_prototype[attribute_name] = null as unknown as ControlProperty;
				const control_node = $.properties.control.ref.key.ref.properties.node;
				instructions.push((contract, data, destructor, getProperties) => {
					const control_wrapper = new ControlControlWrapper(
						destructor,
						control_node,
						attribute_name,
						data,
						getProperties
					);
					contract[attribute_name] = control_wrapper.initializer;
				});
				break;
			}
			case "instruction": {
				contract_prototype[attribute_name] = noop;
				instructions.push((contract, data, destructor, getProperties) => {
					let handler: (...args: any) => void;
					const sync_wrapper = new GenericSyncher(
						() => {
							(getProperties()[attribute_name] as ControlInstructionProperty)(handler);
						},
						() => {
							data[attribute_name] = handler;
						},
						destructor
					);
					contract[attribute_name] = (h: (...args: any) => void) => {
						handler = h;
						sync_wrapper.update();
					};
				});
				break;
			}
			case "markup": {
				contract_prototype[attribute_name] = noop;
				instructions.push((contract, _data, _destructor, getProperties) => {
					contract[attribute_name] = () => {
						return (getProperties()[attribute_name] as ControlMarkupProperty)();
					};
				});
				break;
			}
			case "property": {
				const property = attribute.properties.type.state.node;
				switch (property.properties.type.state.name) {
					case "collection": {
						const $ = property.properties.type.state.node;
						contract_prototype[attribute_name] = {};
						const propertiesConstructor = createPropertiesWrapperConstructor($.properties.node);
						instructions.push((contract, data, _destructor, getProperties) => {
							const collection_data: CollectionData = (data[attribute_name] = {
								entries: new Map(),
								sort: [],
							});
							const wrapper = new CollectionWrapper(
								collection_data,
								() => {
									return getProperties()[attribute_name]! as ControlCollectionProperty;
								},
								(entry_data, entry_destructor, getEntryProperties) => {
									return propertiesConstructor(entry_data, entry_destructor, getEntryProperties);
								}
							);
							contract[attribute_name] = wrapper.initializer;
						});

						break;
					}
					case "number": {
						contract_prototype[attribute_name] = noop;
						instructions.push((contract, data, destructor, getProperties) => {
							const wrapper = new NumberPropertyWrapper(attribute_name, data, destructor, getProperties);
							const update_function: ControlNumberProperty = (value) => {
								wrapper.update(value);
							};
							contract[attribute_name] = update_function;
						});
						break;
					}
					case "state group": {
						const state_group = property.properties.type.state.node;
						contract_prototype[attribute_name] = {};
						const state_constructors = new Map<
							string,
							(data: ControlData, destructor: Destructor, getProperties: () => ControlProperties) => ControlProperties
						>();
						for (const [name, state] of state_group.properties.states) {
							(contract_prototype[attribute_name] as ControlStateGroupProperty)[state.key] =
								noop as unknown as ControlStateGroupProperty[1];
							state_constructors.set(name, createPropertiesWrapperConstructor(state.properties.node));
						}
						instructions.push((contract, data, _destructor, getProperties) => {
							let proxy: any | undefined = undefined;
							Object.defineProperty(contract, attribute_name, {
								get: () => {
									if (proxy !== undefined) return proxy;
									const local_proxy = new Proxy(
										{},
										{
											get: (_, name: string) => {
												return (state_destructor: Destructor, state_data: ControlData) => {
													const state_wrapper = new StateWrapper(
														state_destructor,
														attribute_name,
														name,
														data,
														state_data,
														() => getProperties()[attribute_name] as ControlStateGroupProperty
													);
													state_wrapper.set_state();
													const propertiesConstructor = state_constructors.get(name)!;
													return propertiesConstructor(state_data, state_destructor, () => state_wrapper.properties);
												};
											},
										}
									);
									proxy = local_proxy;
									return proxy;
								},
							});
						});
						break;
					}
					case "text": {
						contract_prototype[attribute_name] = noop;
						instructions.push((contract, data, destructor, getProperties) => {
							const wrapper = new TextPropertyWrapper(attribute_name, data, destructor, getProperties);
							const func: ControlTextProperty = (value) => {
								wrapper.update(value);
							};
							contract[attribute_name] = func;
						});
						break;
					}
					default:
						unexpectedState(property.properties.type.state);
				}
				break;
			}
			default:
				unexpectedState(attribute.properties.type.state);
		}
	}

	return (data, destructor, getProperties) => {
		const contract: ControlProperties = Object.create(contract_prototype);
		const instruction_length = instructions.length;
		for (let i = 0; i < instruction_length; i++) {
			instructions[i](contract, data, destructor, getProperties);
		}
		return contract;
	};
}

abstract class SynchronizedProperty {
	private sync_requested = false;
	protected destroyed = false;
	constructor(destructor: Destructor | undefined) {
		destructor?.(() => {
			this.destroyed = true;
			deferred_control_calls.delete(this);
		});
	}
	protected requestSync() {
		if (this.sync_requested) {
			return;
		}
		requestSync();
		this.sync_requested = true;
		onSyncData(this);
	}
	public doSync(): void {
		this.sync_requested = false;
		if (!this.destroyed) {
			this.sync();
		}
	}
	public abstract sync(): void;
	public abstract syncData(): void;
}

class GenericSyncher extends SynchronizedProperty {
	constructor(private sync_callback: () => void, private sync_data: () => void, destructor: Destructor | undefined) {
		super(destructor);
	}
	sync(): void {
		this.sync_callback();
	}
	syncData(): void {
		this.sync_data();
	}
	update() {
		this.requestSync();
	}
}

class CollectionWrapper extends SynchronizedProperty {
	private initialized_collection?: ReturnType<ControlCollectionProperty["initialize"]>;
	private initialized_entries = new Map<
		string,
		{
			entry: ControlProperties;
			remove: () => void;
		}
	>();
	private entries_to_add = new Map<
		string,
		{
			data: ControlData;
			contract: ControlProperties;
			destructor: Destructor;
		}
	>();
	private entries_to_remove = new Set<string>();
	private sorting: string[] = [];
	private destructor: Destructor;
	private should_sort = false;
	constructor(
		private data: CollectionData,
		private control_implementation: () => ControlCollectionProperty,
		private entryContract: (
			data: ControlData,
			entry_destructor: Destructor,
			getProperties: () => ControlProperties
		) => ControlProperties
	) {
		super(undefined);
	}

	get initializer(): ControlCollectionProperty {
		return {
			initialize: (destructor) => {
				destructor(() => {
					deferred_control_calls.delete(this);
				});
				this.requestSync();
				this.destructor = destructor;
				return {
					add: (key, entry_destructor, entry_data) => {
						this.requestSync();
						const entry_contract = this.entryContract(entry_data, entry_destructor, () => {
							//FIXME remove the sync calls or explain why it is needed
							this.syncData();
							this.sync();
							return this.initialized_entries.get(key)!.entry;
						});
						this.entries_to_add.set(key, {
							data: entry_data,
							destructor: entry_destructor,
							contract: entry_contract,
						});
						return {
							entry: entry_contract,
							remove: () => {
								this.requestSync();
								if (this.entries_to_add.has(key)) {
									this.entries_to_add.delete(key);
								} else {
									this.entries_to_remove.add(key);
								}
							},
						};
					},
					sort: (sorting) => {
						this.sorting = sorting;
						this.should_sort = true;
						this.requestSync();
					},
				};
			},
		};
	}
	syncData(): void {
		for (const entry of this.entries_to_remove) {
			this.data.entries.delete(entry);
		}
		for (const [key, entry] of this.entries_to_add) {
			this.data.entries.set(key, entry.data);
		}
		this.data.sort = this.sorting ?? this.data.sort ?? [];
	}
	sync(): void {
		if (this.initialized_collection === undefined) {
			this.initialized_collection = this.control_implementation().initialize(
				DestructorWrapper.wrap(this.destructor),
				this.data
			);
		}
		for (const entry of this.entries_to_remove) {
			const initialized_entry = this.initialized_entries.get(entry);
			if (initialized_entry !== undefined) {
				initialized_entry.remove();
			}
		}

		for (const [key, entry] of this.entries_to_add) {
			const initialized_entry = this.initialized_collection.add(
				key,
				DestructorWrapper.wrap(entry.destructor),
				entry.data
			);
			this.initialized_entries.set(key, initialized_entry);
		}
		if (this.should_sort === true) {
			this.initialized_collection.sort?.(this.sorting);
		}
		this.entries_to_add.clear();
		this.entries_to_remove.clear();
		this.should_sort = false;
	}
}

class DestructorWrapper extends SynchronizedProperty {
	private callback: (() => void) | undefined = undefined;
	private should_call_callback = false;
	constructor(private destructor: Destructor) {
		super(undefined);
		this.destructor(() => {
			this.should_call_callback = true;
			this.requestSync();
		});
	}
	sync(): void {
		if (this.should_call_callback) {
			this.callback?.();
		}
	}
	syncData() {
		// nothing to synchronize.
	}
	get wrapped_destructor(): Destructor {
		return (callback) => {
			if (this.should_call_callback) {
				callback();
			} else {
				this.callback = callback;
			}
		};
	}

	static wrap(destructor: Destructor): Destructor {
		return new DestructorWrapper(destructor).wrapped_destructor;
	}
}

class StateWrapper extends SynchronizedProperty {
	private initialized_properties: ControlProperties;
	constructor(
		private destructor: Destructor,
		private state_group_name: string,
		private name: string,
		private parent_data: ControlData,
		private data: ControlData,
		private control_implementation: () => ControlStateGroupProperty
	) {
		super(destructor);
	}
	get properties() {
		if (this.initialized_properties === undefined) {
			this.initialized_properties = this.control_implementation()[this.name](
				DestructorWrapper.wrap(this.destructor),
				this.data
			);
		}
		return this.initialized_properties;
	}
	set_state(): void {
		this.requestSync();
	}
	sync(): void {
		this.initialized_properties = this.control_implementation()[this.name](
			DestructorWrapper.wrap(this.destructor),
			this.data
		);
	}
	syncData() {
		this.parent_data[this.state_group_name] = {
			name: this.name,
			node: this.data,
		};
	}
}

class TextPropertyWrapper extends SynchronizedProperty {
	private value: string;
	constructor(
		private name: string,
		private data: ControlData,
		destructor: Destructor,
		private getProperties: () => ControlProperties
	) {
		super(destructor);
	}
	update(value: string): void {
		this.value = value;
		this.requestSync();
	}
	syncData(): void {
		this.data[this.name] = this.value;
	}
	sync(): void {
		(this.getProperties()[this.name] as ControlTextProperty)(this.value);
	}
}

class NumberPropertyWrapper extends SynchronizedProperty {
	private value: number;
	constructor(
		private name: string,
		private data: ControlData,
		destructor: Destructor,
		private getProperties: () => ControlProperties
	) {
		super(destructor);
	}
	update(value: number): void {
		this.value = value;
		this.requestSync();
	}
	syncData(): void {
		this.data[this.name] = this.value;
	}
	sync(): void {
		(this.getProperties()[this.name] as ControlNumberProperty)(this.value);
	}
}

class ControlControlWrapper extends SynchronizedProperty {
	private initialized_control: ControlProperties;
	private property_wrappers: ControlProperties;
	private data: ControlData = {};

	constructor(
		private destructor: Destructor,
		private node: api_controls.Ccontrol_node,
		private attribute_name: string,
		private parent_data: ControlData,
		private getProperties: () => ControlProperties
	) {
		super(destructor);
		this.requestSync();
		let properties_constructor = controlConstructors.get(this.node);
		if (properties_constructor === undefined) {
			properties_constructor = createPropertiesWrapperConstructor(this.node);
			controlConstructors.set(this.node, properties_constructor);
		}
		this.property_wrappers = properties_constructor(this.data, destructor, () => {
			return this.initialized_control;
		});
	}

	get properties(): ControlProperties {
		return this.property_wrappers;
	}

	get initializer(): ControlControlProperty {
		return (control_destructor) => {
			this.destructor = control_destructor;
			return this.property_wrappers;
		};
	}
	sync(): void {
		this.initialized_control = (this.getProperties()[this.attribute_name] as ControlControlProperty)(
			DestructorWrapper.wrap(this.destructor),
			this.data
		);
	}
	syncData(): void {
		this.parent_data[this.attribute_name] = this.data;
	}
}

type HTMLElementConstructor = () => HTMLElement;

const controlConstructors = new Map<
	api_controls.Ccontrol_node,
	ReturnType<typeof createPropertiesWrapperConstructor>
>();

class ControlWrapper extends SynchronizedProperty {
	private initialized_control: ControlProperties;
	private property_wrappers: ControlProperties;
	private data: ControlData = {};

	constructor(
		private initializer: ControlContract,
		private parent: HTMLElementConstructor,
		private view_root: HTMLElementConstructor,
		private destructor: Destructor,
		private node: api_controls.Ccontrol_node
	) {
		super(destructor);
		this.requestSync();
		let properties_constructor = controlConstructors.get(this.node);
		if (properties_constructor === undefined) {
			properties_constructor = createPropertiesWrapperConstructor(this.node);
			controlConstructors.set(this.node, properties_constructor);
		}
		this.property_wrappers = properties_constructor(this.data, destructor, () => {
			return this.initialized_control;
		});
	}
	get properties(): ControlProperties {
		return this.property_wrappers;
	}
	sync(): void {
		if (this.initialized_control === undefined) {
			this.initialized_control = this.initializer(this.parent(), this.view_root(), this.destructor, this.data);
		}
	}
	syncData(): void {
		//noop
	}
}

function noop() {
	//noop
}
